import React, { memo, useCallback, useMemo, useState } from 'react';
import { View, Text, Image, TouchableOpacity, StyleSheet } from 'react-native';
import type { CorkBoardReply } from '@/types/residents/corkBoard';
import { AttachmentPreview } from './AttachmentPreview';
import { AdaptiveMenu } from './AdaptiveMenu';
import { CORK_BOARD_COLORS, corkBoardStyles } from './corkBoardStyles';
import { SEMANTIC_COLORS } from '@/constants/residents/residentColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { formatRelativeDate } from '@/utils/common/formatting';

interface ReplyCardProps {
  reply: CorkBoardReply;
  isOP: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onReport?: () => void;
  onImagePress?: (imageUrls: string[], index: number) => void;
  onDocumentPress?: (url: string, title: string) => void;
}

export const ReplyCard: React.FC<ReplyCardProps> = memo(({
  reply,
  isOP,
  onEdit,
  onDelete,
  onReport,
  onImagePress,
  onDocumentPress,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const hasActions = reply.isOwnReply ? (!!onEdit || !!onDelete) : !!onReport;

  const openMenu = useCallback(() => setShowMenu(true), []);
  const closeMenu = useCallback(() => setShowMenu(false), []);

  const accessibilityLabel = useMemo(() => {
    const parts = [reply.creator.name];
    if (isOP) parts.push('original poster');
    const preview = reply.body.length > 100 ? `${reply.body.slice(0, 100)}...` : reply.body;
    parts.push(preview);
    return parts.join(', ');
  }, [reply, isOP]);

  const handleEdit = useCallback(() => {
    closeMenu();
    onEdit?.();
  }, [onEdit, closeMenu]);

  const handleDelete = useCallback(() => {
    closeMenu();
    onDelete?.();
  }, [onDelete, closeMenu]);

  const handleReport = useCallback(() => {
    closeMenu();
    onReport?.();
  }, [onReport, closeMenu]);

  return (
    <View style={styles.container} accessibilityLabel={accessibilityLabel}>
      {/* Header row: avatar, name, OP badge, timestamp, overflow */}
      <View style={styles.headerRow}>
        <View style={styles.authorSection}>
          {reply.creator.photoUrl ? (
            <Image source={{ uri: reply.creator.photoUrl }} style={styles.avatarImage} accessibilityIgnoresInvertColors />
          ) : (
            <View style={corkBoardStyles.avatar}>
              <Text style={corkBoardStyles.avatarText}>{reply.creator.initials}</Text>
            </View>
          )}
          <Text style={styles.authorName} numberOfLines={1}>{reply.creator.name}</Text>
          {isOP && (
            <View style={styles.opBadge}>
              <Text style={styles.opBadgeText}>OP</Text>
            </View>
          )}
          <Text style={styles.dot}>·</Text>
          <Text style={styles.timestamp}>{formatRelativeDate(reply.createdAt)}</Text>
        </View>
        {hasActions && (
          <View style={styles.menuAnchor}>
            <TouchableOpacity
              style={styles.menuButton}
              onPress={openMenu}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              accessibilityRole="button"
              accessibilityLabel="Reply actions"
            >
              <RESIDENTS_SVG_COMPONENTS.THREE_DOTS width={18} height={18} color={SEMANTIC_COLORS.text.secondary} />
            </TouchableOpacity>

            <AdaptiveMenu visible={showMenu} onClose={closeMenu}>
              <View style={styles.sheetContent}>
                {reply.isOwnReply ? (
                  <>
                    {onEdit && (
                      <TouchableOpacity style={styles.sheetItem} onPress={handleEdit} activeOpacity={0.7} accessibilityRole="button" accessibilityLabel="Edit reply">
                        <Text style={styles.sheetItemText}>Edit reply</Text>
                      </TouchableOpacity>
                    )}
                    {onDelete && (
                      <TouchableOpacity style={styles.sheetItem} onPress={handleDelete} activeOpacity={0.7} accessibilityRole="button" accessibilityLabel="Delete reply">
                        <Text style={styles.sheetItemTextDanger}>Delete reply</Text>
                      </TouchableOpacity>
                    )}
                  </>
                ) : (
                  onReport && (
                    <TouchableOpacity style={styles.sheetItem} onPress={handleReport} activeOpacity={0.7} accessibilityRole="button" accessibilityLabel="Report reply">
                      <Text style={styles.sheetItemTextDanger}>Report reply</Text>
                    </TouchableOpacity>
                  )
                )}
              </View>
            </AdaptiveMenu>
          </View>
        )}
      </View>

      {/* Body */}
      <Text style={styles.body}>{reply.body}</Text>

      {/* Attachments */}
      {reply.attachments.length > 0 && (
        <AttachmentPreview
          attachments={reply.attachments}
          onImagePress={onImagePress}
          onDocumentPress={onDocumentPress}
        />
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    backgroundColor: CORK_BOARD_COLORS.cardBackground,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: CORK_BOARD_COLORS.cardBorder,
    padding: 16,
    gap: 8,
    overflow: 'visible',
    zIndex: 1,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    overflow: 'visible',
    zIndex: 10,
  },
  authorSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
    minWidth: 0,
  },
  avatarImage: {
    width: 28,
    height: 28,
    borderRadius: 14,
  },
  authorName: {
    fontSize: 13,
    fontWeight: '500',
    color: SEMANTIC_COLORS.text.primary,
    flexShrink: 1,
  },
  opBadge: {
    backgroundColor: SEMANTIC_COLORS.interactive.primary,
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 1,
  },
  opBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#FFFFFF',
    letterSpacing: 0.5,
  },
  dot: {
    fontSize: 12,
    color: SEMANTIC_COLORS.text.muted,
  },
  timestamp: {
    fontSize: 12,
    color: SEMANTIC_COLORS.text.muted,
  },
  menuAnchor: {
    position: 'relative',
  },
  menuButton: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sheetContent: {
    gap: 4,
  },
  sheetItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 14,
    paddingHorizontal: 4,
    borderRadius: 8,
  },
  sheetItemText: {
    fontSize: 16,
    color: SEMANTIC_COLORS.text.primary,
    fontWeight: '400',
  },
  sheetItemTextDanger: {
    fontSize: 16,
    color: SEMANTIC_COLORS.interactive.danger,
    fontWeight: '400',
  },
  body: {
    fontSize: 14,
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 21,
  },
});
